import React, { forwardRef } from 'react';

import { useSelector } from 'react-redux';
import { Link as RRLink } from 'react-router-dom';
import {
	Select,
	MenuItem,
	ListItemText,
	FormControl,
	InputLabel,
	ListItemIcon,
	SelectChangeEvent,
} from '@mui/material';

import HasFeature from 'components/features/HasFeature';
import { ProductApi } from 'common/types';

import { useTranslation } from 'services/localization/useTranslation';
import { Routes } from 'routing';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { RiAddLine } from 'react-icons/ri';

type vatPercent = ProductApi['vatPercent'];
interface Props {
	value?: vatPercent | undefined;
	onChange?: (value: vatPercent) => void;
	disabled?: boolean;
	noMargin?: boolean;
}

const VatSelect = forwardRef((props: Props, ref) => {
	const { value, onChange, noMargin } = props;
	const { t } = useTranslation();

	const vatTable = useSelector(ShopSelectors.activeShopVatTable);

	const handleChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		const numberValue = Number(value);
		onChange?.(isNaN(numberValue) ? 0 : numberValue);
	};

	return (
		<FormControl fullWidth margin={noMargin ? 'none' : 'normal'}>
			<InputLabel htmlFor="tax-percentage-select" variant="standard" disabled={props.disabled}>
				{t('common:taxation.taxPercentage', 'Tax percentage')}
			</InputLabel>
			<Select
				id="tax-percentage-select"
				fullWidth
				variant="standard"
				value={value?.toString()}
				onChange={handleChange}
				renderValue={(percentage) => `${percentage}%`}
				disabled={props.disabled}
			>
				{vatTable.vatValues.map((vatItem) => (
					<MenuItem key={vatItem.value} value={vatItem.value}>
						<ListItemText primary={`${vatItem.value}%`} secondary={vatItem.description} />
					</MenuItem>
				))}
				<HasFeature requiredFeature="TAXES">
					<RRLink to={Routes.ACCOUNT_TAXES}>
						<MenuItem>
							<ListItemIcon>
								<RiAddLine />
							</ListItemIcon>
							<ListItemText secondary={t('shop.addTax')} />
						</MenuItem>
					</RRLink>
				</HasFeature>
			</Select>
		</FormControl>
	);
});

export default VatSelect;
